/* Serialize the theme's recognized cart/change mutations and reconcile
 * quantity responses so a stale response can never render (or flag a false
 * "You can only add N…" quantity error) over a newer quantity intent.
 *
 * Scope: POST /cart/change with either the vendor JSON body shape
 * {line, quantity}, or a bounded key-based shape used by Shipping Protection
 * ({id, quantity}) and PZ exact-line state writes ({id, properties}). FormData,
 * /cart/add, Request-object calls and every other request pass through
 * untouched (fail-open: an unrecognized mutation shape keeps native behavior,
 * it is never queued).
 * Loads after sc-cart-mutation-recovery.js so the recovery layer only ever
 * sees serialized (non-overlapping) mutations.
 *
 * State machine invariants:
 * - At most ONE operation — a forwarded mutation OR a reconciliation fetch —
 *   may commit/render at any time. New mutations queue while either runs.
 * - A client timeout never releases the queue while the in-flight request
 *   could still commit: the request is ABORTED (its caller rejects, its late
 *   settle is discarded and can never render). Without an AbortController
 *   (caller-supplied signal we cannot combine) the machine keeps waiting.
 * - A response withheld as stale (a newer intent was already queued when it
 *   settled) is NEVER rendered later — on chain failure the parked callers
 *   reconcile to freshly fetched state or reject; they never fall back to
 *   the withheld body (replaying it resurrected removed rows on screen).
 * - Reconciliation reads cart.js FIRST, then the sections for it. The two
 *   GETs are sequential, not atomic — a mutation between them cannot happen
 *   from this tab (the machine is busy) and cross-tab drift self-heals on
 *   the next render. Completions carry a generation token; a completion for
 *   a superseded generation is discarded. */
(function () {
  'use strict';

  if (typeof window.fetch !== 'function' || window.__scCartQuantityRaceGuard) return;
  window.__scCartQuantityRaceGuard = true;

  /* If an in-flight request exceeds this, abort it and continue the chain —
   * never leave the queue pinned behind a hung connection, and never let the
   * hung request keep the right to render. */
  var FLUSH_TIMEOUT_MS = 20000;
  var READ_TIMEOUT_MS = FLUSH_TIMEOUT_MS;

  var upstreamFetch = window.fetch;
  var activeOp = null;      // forwarded mutation currently allowed to commit
  var reconciling = false;  // reconciliation fetch currently allowed to render
  var opSeq = 0;            // generation token: one per started operation
  var queue = [];
  var held = [];
  var flushTimer = null;
  var authoritativeLines = null; // latest server key -> 1-based line map for this queued chain
  var rotations = Object.create(null); // replaced line key -> successor key within this queued chain
  var cartWrite = null; // runtime read -> POST -> proof reservation, in THIS queue
  var propertyWriteUncertain = false;

  function coordinatorError() {
    var error = new Error('Your cart changed. Please check the quantity and reload your cart.');
    error.code = 'E_CART_CHANGE_COORDINATOR_UNAVAILABLE';
    error.definite = true;
    error.dispatched = false;
    return error;
  }

  function reserveCartWrite() {
    var lease = {
      release: function (outcome) {
        if (cartWrite !== lease) return;
        cartWrite = null;
        if (outcome && outcome.ambiguous) {
          // A browser abort or an inconclusive GET is not server non-commit.
          // Refuse queued changes; never replay them behind an unknown POST.
          // Only PZ Edit/writeback stay off until reload; NEW shopper writes
          // (drawer qty, Shipping Protection) pass as on F1, ATC never waits.
          propertyWriteUncertain = true;
          var pending = queue.splice(0).concat(held.splice(0).map(function (h) { return h.entry; }));
          for (var i = 0; i < pending.length; i += 1) pending[i].reject(coordinatorError());
          forwardNext();
          return;
        }
        var cart = outcome && outcome.cart;
        var line = outcome && outcome.line;
        if (setAuthoritativeLines(cart) && line && outcome.oldLineKey && line.key
          && !Object.prototype.hasOwnProperty.call(authoritativeLines, outcome.oldLineKey)
          && Object.prototype.hasOwnProperty.call(authoritativeLines, line.key)) {
          // The exact transport proof supplies the successor, NOT its position.
          rotations[outcome.oldLineKey] = line.key;
        }
        if (!cart && hasQueuedQuantity()) recoverAuthoritativeLinesBeforeQueue();
        else forwardNext();
      }
    };
    cartWrite = lease;
    return lease;
  }

  // Background metadata enrichment never takes priority over shopper intent.
  // This admission lease shares the EXISTING change queue; it is not a second
  // transport or an assertion that an on-wire POST can be cancelled safely.
  var foregroundCount = 0;
  var foregroundEpoch = 0;
  var backgroundLease = null;
  var backgroundAmbiguous = false;
  var unresolvedAdds = Object.create(null);
  window.__pzCartWritePriority = {
    // Same-page controlled writers only. External apps/tabs/devices do not
    // participate, so this is deliberately NOT a Shopify/server CAS claim.
    acquireCartWrite: function (context) {
      var signal = context && context.signal;
      if (propertyWriteUncertain || (signal && signal.aborted)) return Promise.reject(coordinatorError());
      return new Promise(function (resolve, reject) {
        var entry = { mutation: { kind: 'transaction' }, reject: reject,
          grant: function () {
            if (propertyWriteUncertain || (signal && signal.aborted)) {
              reject(coordinatorError());
              forwardNext();
            } else resolve(reserveCartWrite());
          } };
        if (activeOp || reconciling || cartWrite || queue.length) queue.push(entry);
        else entry.grant();
      });
    },
    /* R2b (qty Edit D3): PZ Edit Save reads the cart through its own iframe transport, so it never
     * passes this queue. It asks here before its pre-replace read. Resolves IMMEDIATELY when no
     * theme cart write is in flight (the normal path costs nothing); otherwise polls until the queue
     * drains, bounded (default 1500 ms, max 5000 ms). Never rejects, never delays ATC. */
    whenCartIdle: function (timeoutMs) {
      function busy() { return !!(activeOp || reconciling || cartWrite || queue.length || held.length); }
      if (!busy()) return Promise.resolve({ waited: false });
      var limit = typeof timeoutMs === 'number' && timeoutMs > 0 ? Math.min(timeoutMs, 5000) : 1500;
      var started = Date.now();
      return new Promise(function (resolve) {
        (function poll() {
          if (!busy()) { resolve({ waited: true, ms: Date.now() - started }); return; }
          if (Date.now() - started >= limit) { resolve({ waited: true, timedOut: true, ms: Date.now() - started }); return; }
          setTimeout(poll, 25);
        })();
      });
    },
    markAtcPending: function (operationId, pending) {
      if (pending) { unresolvedAdds[operationId] = true; foregroundEpoch += 1; }
      else delete unresolvedAdds[operationId];
    },
    beginForeground: function () {
      foregroundCount += 1;
      foregroundEpoch += 1;
      var released = false;
      return function () {
        if (released) return;
        released = true;
        foregroundCount -= 1;
      };
    },
    tryWritebackLease: function (operationId) {
      if (!operationId || foregroundCount || activeOp || reconciling || cartWrite || propertyWriteUncertain || queue.length || held.length
        || backgroundLease || backgroundAmbiguous || Object.keys(unresolvedAdds).length || document.hidden
        || /\/checkouts?(?:\/|$)/.test(window.location.pathname)) return null;
      var epoch = foregroundEpoch;
      var reservation = reserveCartWrite();
      var lease = {
        dispatched: false,
        markDispatched: function () { lease.dispatched = true; },
        canDispatch: function () {
          return backgroundLease === lease && foregroundEpoch === epoch && !foregroundCount
            && !activeOp && !reconciling && !queue.length && !held.length && !Object.keys(unresolvedAdds).length && !document.hidden;
        },
        release: function (outcome) {
          if (backgroundLease !== lease) return;
          backgroundLease = null;
          if (outcome && outcome.ambiguous) backgroundAmbiguous = true;
          reservation.release(outcome);
        }
      };
      backgroundLease = lease;
      return lease;
    }
  };

  window.fetch = function (resource, init) {
    var mutation = null;
    try {
      mutation = readSerializedCartMutation(resource, init);
    } catch (error) {
      mutation = null;
    }
    if (!mutation) {
      return upstreamFetch.apply(this, arguments);
    }
    foregroundEpoch += 1; // queued quantity/remove/change invalidates an older read lease
    // A background READ may yield immediately. After its POST dispatch the
    // quantity intent queues until terminal proof; revocation cannot undo it.
    if (backgroundLease && !backgroundLease.dispatched) backgroundLease.release();

    return new Promise(function (resolve, reject) {
      var entry = {
        resource: resource,
        init: init,
        mutation: mutation,
        resolve: resolve,
        reject: reject
      };
      if (activeOp || reconciling || cartWrite) {
        queue.push(entry);
      } else {
        forward(entry);
      }
    });
  };

  function readSerializedCartMutation(resource, init) {
    var url = typeof resource === 'string'
      ? resource
      : (typeof Request !== 'undefined' && resource instanceof Request ? resource.url : '');
    var method = String((init && init.method)
      || (typeof Request !== 'undefined' && resource instanceof Request && resource.method)
      || 'GET').toUpperCase();
    if (method !== 'POST' || !/\/cart\/change(?:\.js)?(?:\?|$)/.test(url)) return null;
    if (!init || typeof init.body !== 'string') return null;

    var payload = JSON.parse(init.body);
    var line = parseInt(payload.line, 10);
    var quantity = parseInt(payload.quantity, 10);
    if (line > 0 && quantity >= 0) {
      var sections = Array.isArray(payload.sections) ? payload.sections : [];
      var surface = sections.indexOf('cart-drawer') !== -1 && sections.indexOf('main-cart-items') === -1
        ? 'drawer'
        : 'page';
      var row = getLineRow(line, surface);
      return {
        kind: 'quantity',
        payload: payload,
        line: line,
        quantity: quantity,
        surface: surface,
        key: row ? row.getAttribute('data-cart-item-key') : null
      };
    }

    var key = typeof payload.id === 'string' ? payload.id : '';
    var hasKeyQuantity = Object.prototype.hasOwnProperty.call(payload, 'quantity') && quantity >= 0;
    var hasProperties = Object.prototype.hasOwnProperty.call(payload, 'properties') && isPlainObject(payload.properties);
    if (!/^[A-Za-z0-9:_-]{1,255}$/.test(key) || (!hasKeyQuantity && !hasProperties)) return null;

    return {
      kind: 'key',
      payload: payload,
      key: key,
      hasProperties: hasProperties,
      isRemoval: hasKeyQuantity && quantity === 0
    };
  }

  function isPlainObject(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    var prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
  }

  function getLineRow(line, surface) {
    var drawerRow = document.getElementById('CartDrawer-Item-' + line);
    var pageRow = document.getElementById('CartItem-' + line);
    return surface === 'drawer' ? (drawerRow || pageRow) : (pageRow || drawerRow);
  }

  /* Both surfaces render the same 1-based cart line numbering, so a key found
   * on either surface names the same line; scan the initiating surface first
   * (mirrors the capture fallback in getLineRow). */
  function findLineByKey(key, surface) {
    var prefixes = surface === 'drawer'
      ? ['CartDrawer-Item-', 'CartItem-']
      : ['CartItem-', 'CartDrawer-Item-'];
    for (var p = 0; p < prefixes.length; p += 1) {
      var prefix = prefixes[p];
      var rows = document.querySelectorAll('[id^="' + prefix + '"][data-cart-item-key]');
      for (var i = 0; i < rows.length; i += 1) {
        if (rows[i].getAttribute('data-cart-item-key') === key) {
          var line = parseInt(rows[i].id.slice(prefix.length), 10);
          if (line > 0) return line;
        }
      }
    }
    return null;
  }

  function staleLineError() {
    return new Error('sc-cart-quantity-race-guard: cart line no longer exists');
  }

  function forward(entry) {
    if (entry.mutation.kind === 'transaction') { entry.grant(); return; }
    /* Rows can shift while an entry waits (a queued removal committed, a
     * re-render renumbered lines). Re-resolve the body's 1-based line from the
     * row key captured at request time before letting it hit the network. */
    var mutation = entry.mutation;
    var args = [entry.resource, entry.init];
    if (mutation.kind === 'key' && mutation.hasProperties && !mutation.isRemoval) {
      mutation.prevLine = currentLineForKey(mutation.key, 'page');
    }
    if (mutation.kind === 'quantity' && mutation.key) {
      mutation.key = resolveRotatedKey(mutation.key);
      var currentLine = currentLineForKey(mutation.key, mutation.surface);
      if (currentLine === null) {
        /* The line is gone (removed while queued): the body's stale 1-based
         * index could now name a different item, so this write must never
         * reach the network. If a newer intent is queued, park this caller so
         * it adopts the chain's final (fresh) state; otherwise reconcile
         * everyone still parked from freshly fetched cart state. */
        if (hasQueuedQuantity()) {
          held.push({ entry: entry, response: null, error: staleLineError() });
          forwardNext();
          return;
        }
        var entries = held.splice(0, held.length).map(function (h) {
          return assign(h.entry, { __scReconcileError: h.error || null });
        }).concat([entry]);
        reconcileEntries(entries);
        return;
      }
      if (currentLine !== mutation.line) {
        var payload = mutation.payload;
        mutation.line = currentLine;
        payload.line = currentLine;
        args[1] = assign({}, entry.init, { body: JSON.stringify(payload) });
      }
    }

    /* Own abort authority whenever possible so a hung request can be cut off
     * definitively. A caller-supplied signal is combined when the platform
     * can; otherwise the caller's signal wins and the timeout must wait. */
    var controller = null;
    var callerSignal = entry.init && entry.init.signal;
    if (typeof AbortController === 'function') {
      controller = new AbortController();
      if (!callerSignal) {
        args[1] = assign({}, args[1] || entry.init, { signal: controller.signal });
      } else if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.any === 'function') {
        args[1] = assign({}, args[1] || entry.init, { signal: AbortSignal.any([callerSignal, controller.signal]) });
      } else {
        controller = null;
      }
    }

    var op = {
      seq: ++opSeq,
      entry: entry,
      controller: controller,
      abandoned: false
    };
    activeOp = op;
    startFlushTimer();

    var responsePromise;
    try {
      responsePromise = upstreamFetch.apply(window, args);
    } catch (error) {
      settle(op, null, error);
      return;
    }
    responsePromise.then(function (response) {
      settle(op, response, null);
    }, function (error) {
      settle(op, null, error);
    });
  }

  function currentLineForKey(key, surface) {
    if (authoritativeLines) {
      return Object.prototype.hasOwnProperty.call(authoritativeLines, key)
        ? authoritativeLines[key]
        : null;
    }
    return findLineByKey(key, surface);
  }

  function resolveRotatedKey(key) {
    var current = key;
    var seen = Object.create(null);
    while (Object.prototype.hasOwnProperty.call(rotations, current) && !seen[current]) {
      seen[current] = true;
      current = rotations[current];
    }
    return current;
  }

  function settle(op, response, error) {
    if (op.abandoned) {
      /* Late completion of a timed-out, aborted operation. Its caller was
       * already rejected; this body must never render or touch state. */
      return;
    }
    if (activeOp !== op) {
      // Defensive: a superseded generation never commits.
      if (response) op.entry.resolve(response); else op.entry.reject(error);
      return;
    }
    clearFlushTimer();

    if (response) {
      /* A keyed removal can renumber cart lines before its caller renders the
       * returned sections. Read the server cart carried by this response and
       * rebase queued line writes from their captured item key, never from the
       * still-stale DOM. Keep activeOp set while the clone is read so no new
       * recognized mutation can slip into this handoff window. */
      refreshAuthoritativeLines(response, op.entry.mutation).then(function (hasAuthoritativeLines) {
        completeSettle(op, response, error, hasAuthoritativeLines);
      });
      return;
    }

    completeSettle(op, response, error, false);
  }

  function completeSettle(op, response, error, hasAuthoritativeLines) {
    if (op.abandoned || activeOp !== op) return;
    activeOp = null;

    if (op.entry.mutation.kind === 'quantity' && hasQueuedQuantity()) {
      /* A newer quantity intent already exists, so this response is stale:
       * withhold it from the theme (its render and its mismatch-triggered
      * "You can only add N…" message would describe an outdated state) and
       * reconcile it against the chain's final state instead. */
      held.push({ entry: op.entry, response: response, error: error });
      if (hasAuthoritativeLines) forwardNext(); else recoverAuthoritativeLinesBeforeQueue();
      return;
    }

    if (op.entry.mutation.kind === 'key') {
      /* Keyed PZ/protection callers verify their own response body. They only
      * need mutual exclusion with quantity requests; substituting a later
       * quantity response could erase their proof or omit their sections. */
      if (response) op.entry.resolve(response); else op.entry.reject(error);
      if (!hasAuthoritativeLines && hasQueuedQuantity()) {
        /* A rejected POST may still have committed server-side. Do not let a
         * line-indexed write use pre-request DOM after that ambiguous outcome;
         * read the cart first and rebase by the captured item key. */
        recoverAuthoritativeLinesBeforeQueue();
      } else {
        forwardNext();
      }
      return;
    }

    finishChain(op.entry, response, error);
  }

  function refreshAuthoritativeLines(response, mutation) {
    return new Promise(function (resolve) {
      var done = false;
      var timer = setTimeout(function () {
        if (done) return;
        done = true;
        authoritativeLines = null;
        resolve(false);
      }, READ_TIMEOUT_MS);

      function finish(cart) {
        if (done) return;
        done = true;
        clearTimeout(timer);
        resolve(setAuthoritativeLines(cart, mutation));
      }

      try {
        response.clone().json().then(finish, function () { finish(null); });
      } catch (error) {
        finish(null);
      }
    });
  }

  function setAuthoritativeLines(cart, mutation) {
    if (!cart || !Array.isArray(cart.items)) {
      authoritativeLines = null;
      return false;
    }
    var lines = Object.create(null);
    for (var i = 0; i < cart.items.length; i += 1) {
      var key = cart.items[i] && cart.items[i].key;
      if (typeof key === 'string' && key) lines[key] = i + 1;
    }
    if (
      mutation
      && mutation.kind === 'key'
      && mutation.hasProperties
      && !mutation.isRemoval
      && mutation.prevLine > 0
      && !Object.prototype.hasOwnProperty.call(lines, mutation.key)
    ) {
      var replacement = cart.items[mutation.prevLine - 1];
      var successorKey = replacement && replacement.key;
      if (typeof successorKey === 'string' && successorKey && successorKey !== mutation.key) {
        rotations[mutation.key] = successorKey;
      }
    }
    authoritativeLines = lines;
    return true;
  }

  function recoverAuthoritativeLinesBeforeQueue() {
    reconciling = true;
    ++opSeq;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    reconciliationJson(root + 'cart.js', { headers: { Accept: 'application/json' } }, 'cart')
      .then(function (cart) {
        if (!setAuthoritativeLines(cart)) throw new Error('cart read invalid');
      })
      .catch(function (error) {
        authoritativeLines = null;
        rejectUnsafeQuantityChain(error);
      })
      .then(function () {
        reconciling = false;
        forwardNext();
      });
  }

  function rejectUnsafeQuantityChain(error) {
    var survivors = [];
    for (var i = 0; i < queue.length; i += 1) {
      if (queue[i].mutation && queue[i].mutation.kind === 'quantity') {
        queue[i].reject(error);
      } else {
        survivors.push(queue[i]);
      }
    }
    queue = survivors;

    var parked = held.splice(0, held.length);
    for (var j = 0; j < parked.length; j += 1) {
      parked[j].entry.reject(parked[j].error || error);
    }
  }

  function finishChain(finalEntry, response, error) {
    var heldEntries = held.splice(0, held.length);
    if (response) {
      syncLaggingSurfaceInputs([finalEntry].concat(heldEntries.map(function (h) { return h.entry; })));
      for (var i = 0; i < heldEntries.length; i += 1) {
        heldEntries[i].entry.resolve(response.clone());
      }
      finalEntry.resolve(response);
      forwardNext();
      return;
    }
    /* Final request failed. The withheld responses are stale by definition —
     * NEVER replay them. The failed caller takes the theme's native error
     * path; parked callers reconcile to freshly fetched state (or reject when
     * even that is unavailable). */
    finalEntry.reject(error);
    if (heldEntries.length > 0) {
      reconcileEntries(heldEntries.map(function (h) {
        return assign(h.entry, { __scReconcileError: h.error || null });
      }));
    } else {
      forwardNext();
    }
  }

  function hasQueuedQuantity() {
    for (var i = 0; i < queue.length; i += 1) {
      if (queue[i].mutation && queue[i].mutation.kind === 'quantity') return true;
    }
    return false;
  }

  /* Fetch authoritative state (cart.js first, then its sections) and settle
   * every given entry with it, in the /cart/change.js response shape. Runs as
   * an operation inside the machine: the queue holds until it finishes and a
   * superseded generation's completion is discarded. */
  function reconcileEntries(entries) {
    reconciling = true;
    var token = ++opSeq;

    var template = entries[entries.length - 1];
    var payload = (template && template.mutation && template.mutation.payload) || {};
    var names = Array.isArray(payload.sections)
      ? payload.sections.filter(function (s) { return typeof s === 'string' && s.length > 0; })
      : [];
    var sectionsUrl = typeof payload.sections_url === 'string' && payload.sections_url
      ? payload.sections_url
      : window.location.pathname;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    function settleFailure() {
      for (var i = 0; i < entries.length; i += 1) {
        entries[i].reject(entries[i].__scReconcileError || staleLineError());
      }
    }

    reconciliationJson(root + 'cart.js', { headers: { Accept: 'application/json' } }, 'cart')
      .then(function (cart) {
        if (!cart || typeof cart !== 'object' || !Array.isArray(cart.items)) {
          throw new Error('cart read invalid');
        }
        if (names.length === 0) return cart;
        return reconciliationJson(
          sectionsUrl + (sectionsUrl.indexOf('?') === -1 ? '?' : '&') + 'sections=' + names.join(','),
          { headers: { Accept: 'application/json' } },
          'sections'
        ).then(function (sections) {
          cart.sections = sections;
          return cart;
        });
      })
      .then(function (cart) {
        if (token !== opSeq) { settleFailure(); return; }
        setAuthoritativeLines(cart);
        var body = JSON.stringify(cart);
        syncLaggingSurfaceInputs(entries.filter(function (e) { return e.mutation; }));
        for (var i = 0; i < entries.length; i += 1) {
          entries[i].resolve(new Response(body, {
            status: 200,
            headers: { 'Content-Type': 'application/json' }
          }));
        }
      })
      .catch(function () {
        settleFailure();
      })
      .then(function () {
        reconciling = false;
        forwardNext();
      });
  }

  function reconciliationJson(resource, init, label) {
    return new Promise(function (resolve, reject) {
      var done = false;
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      var requestInit = controller ? assign({}, init, { signal: controller.signal }) : init;
      var timer = setTimeout(function () {
        if (done) return;
        done = true;
        if (controller) {
          try { controller.abort(); } catch (error) { /* already settled */ }
        }
        reject(new Error('sc-cart-quantity-race-guard: reconciliation timed out after ' + READ_TIMEOUT_MS + 'ms'));
      }, READ_TIMEOUT_MS);

      function finish(callback, value) {
        if (done) return;
        done = true;
        clearTimeout(timer);
        callback(value);
      }

      var request;
      try {
        request = upstreamFetch.call(window, resource, requestInit);
      } catch (error) {
        finish(reject, error);
        return;
      }
      request.then(function (response) {
        if (!response.ok) {
          finish(reject, new Error(label + ' read failed ' + response.status));
          return;
        }
        var body;
        try {
          body = response.json();
        } catch (error) {
          finish(reject, error);
          return;
        }
        Promise.resolve(body).then(function (json) {
          finish(resolve, json);
        }, function (error) {
          finish(reject, error);
        });
      }, function (error) {
        finish(reject, error);
      });
    });
  }

  /* The theme's quantity-mismatch check reads `Quantity-{line}` (cart page)
   * before `Drawer-quantity-{line}`, but a drawer-scoped mutation never
   * re-renders the page section, so on /cart the check compares against a
   * stale page input and reports a false limit even for a lone request.
   * Mirroring the initiating drawer input onto the page input keeps the
   * comparison anchored to the user's current intent — a genuine server clamp
   * still differs from it and still surfaces the real error. */
  function syncLaggingSurfaceInputs(entries) {
    var seen = {};
    for (var i = 0; i < entries.length; i += 1) {
      var mutation = entries[i].mutation;
      if (!mutation || mutation.kind !== 'quantity' || mutation.surface !== 'drawer' || seen[mutation.line]) continue;
      seen[mutation.line] = true;
      var drawerInput = document.getElementById('Drawer-quantity-' + mutation.line);
      var pageInput = document.getElementById('Quantity-' + mutation.line);
      if (drawerInput && pageInput && pageInput.value !== drawerInput.value) {
        pageInput.value = drawerInput.value;
      }
    }
  }

  function forwardNext() {
    if (activeOp || reconciling || cartWrite) return;
    if (queue.length === 0) {
      authoritativeLines = null;
      rotations = Object.create(null);
      return;
    }
    forward(queue.shift());
  }

  function startFlushTimer() {
    clearFlushTimer();
    flushTimer = setTimeout(onFlushTimeout, FLUSH_TIMEOUT_MS);
  }

  function clearFlushTimer() {
    if (flushTimer !== null) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
  }

  /* The in-flight request exceeded the safety window. If it can be aborted,
   * cut it off: its caller rejects now, its late settle is discarded, and the
   * chain continues serialized (each queued body re-resolves its line by key
   * in forward()). If it cannot be aborted (caller-owned signal), keep
   * waiting — releasing the queue while it could still commit would let two
   * mutations overlap and invert server order. */
  function onFlushTimeout() {
    flushTimer = null;
    var op = activeOp;
    if (!op) return;
    if (!op.controller) {
      startFlushTimer();
      return;
    }
    op.abandoned = true;
    backgroundAmbiguous = true; // abort cannot prove server non-commit: no background writer this page
    propertyWriteUncertain = true; // no PZ read/write transaction may enter after an unknown quantity POST
    activeOp = null;
    op.entry.reject(new Error('sc-cart-quantity-race-guard: request aborted after ' + FLUSH_TIMEOUT_MS + 'ms'));
    try { op.controller.abort(); } catch (e) { /* already settled */ }
    if (hasQueuedQuantity()) {
      /* Abort only proves the browser stopped waiting; the server may already
       * have committed a keyed removal and renumbered the cart. Re-establish
       * the authoritative key map before any queued line write can run. */
      recoverAuthoritativeLinesBeforeQueue();
    } else if (held.length > 0) {
      reconcileEntries(held.splice(0, held.length).map(function (h) {
        return assign(h.entry, { __scReconcileError: h.error || null });
      }));
    } else if (queue.length > 0) {
      forwardNext();
    }
  }

  function assign(target) {
    for (var i = 1; i < arguments.length; i += 1) {
      var source = arguments[i];
      if (!source) continue;
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) target[key] = source[key];
      }
    }
    return target;
  }
})();
